import type { PlanData, PlanDay } from './types'

export function tryParse(raw: string): PlanData | null {
  function cleanRaw(s: string): string {
    let cleaned = s
      .trim()
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/, '')
      .replace(/\s*```$/, '')
    cleaned = cleaned
      .split('')
      .filter(c => { const n = c.charCodeAt(0); return n >= 32 || n === 9 || n === 10 || n === 13 })
      .join('')
    const fb = cleaned.indexOf('{'), lb = cleaned.lastIndexOf('}')
    return fb !== -1 && lb !== -1 ? cleaned.slice(fb, lb + 1) : cleaned
  }

  function fixCommas(s: string): string {
    return s.replace(/,(\s*[}\]])/g, '$1')
  }

  function fixQuotes(s: string): string {
    let out = '', inStr = false
    for (let i = 0; i < s.length; i++) {
      const c = s[i]!
      if (!inStr) {
        if (c === '"') inStr = true
        out += c
      } else {
        if (c === '\\') { out += c + (s[++i] ?? '') }
        else if (c === '"') {
          let j = i + 1
          while (j < s.length && ' \t\r\n'.includes(s[j]!)) j++
          const nxt = s[j]
          if (!nxt || ':,}]'.includes(nxt)) { inStr = false; out += c }
          else { out += '\\"' }
        } else { out += c }
      }
    }
    return out
  }

  const s = cleanRaw(raw)
  // Attempt 1: clean parse
  try { return JSON.parse(s) as PlanData } catch (_) { /* fall through */ }
  // Attempt 2: fix trailing commas
  try { return JSON.parse(fixCommas(s)) as PlanData } catch (_) { /* fall through */ }
  // Attempt 3: fix unescaped quotes
  try { return JSON.parse(fixQuotes(s)) as PlanData } catch (_) { /* fall through */ }
  // Attempt 4: both fixes
  try { return JSON.parse(fixCommas(fixQuotes(s))) as PlanData } catch (_) { /* fall through */ }
  // Attempt 5: nuke all backslashes (lossy but recoverable)
  try { return JSON.parse(fixCommas(s.replace(/\\/g, ''))) as PlanData } catch (_) { /* fall through */ }
  // Attempt 6: extract individual complete day objects from truncated output
  const wtStart = s.indexOf('"week_template"')
  if (wtStart !== -1) {
    const arrStart = s.indexOf('[', wtStart)
    if (arrStart !== -1) {
      const days: PlanDay[] = []
      let ei = arrStart + 1, edepth = 0, objStart = -1
      while (ei < s.length) {
        const c = s[ei]!
        if (c === '{') { if (edepth === 0) objStart = ei; edepth++ }
        else if (c === '}') {
          edepth--
          if (edepth === 0 && objStart !== -1) {
            try {
              const d = JSON.parse(fixCommas(fixQuotes(s.slice(objStart, ei + 1)))) as PlanDay
              if (d.day_of_week) days.push(d)
            } catch (_) { /* skip malformed day */ }
            objStart = -1
          }
        }
        ei++
      }
      if (days.length > 0) {
        const nm = s.match(/"plan_name"\s*:\s*"([^"]+)"/)
        const tw = s.match(/"total_weeks"\s*:\s*(\d+)/)
        return {
          plan_name: nm?.[1] ?? 'Training Plan',
          total_weeks: tw ? parseInt(tw[1]!) : 8,
          week_template: days,
          phases: [],
          _partial: true,
        }
      }
    }
  }
  return null
}
